/**
 * Pure auth-token + fetch helpers — extracted from AuthProvider so modules
 * outside the React component tree (e.g. the idle tracker) can import them
 * without dragging a .tsx file into TypeScript projects that don't have
 * JSX enabled (`client/tsconfig.json`).
 *
 * AuthProvider continues to export these names for back-compat — every
 * other call site keeps importing from `../providers/AuthProvider`.
 */

import { redirectToLogin } from './login-redirect';

/** Module-scoped dedupe — overlapping refresh calls coalesce to one
 *  in-flight request so a flurry of activity doesn't spam /auth/refresh. */
let refreshPromise: Promise<boolean> | null = null;

/**
 * Rotate the access cookie via the BFF. Returns true on success. Does NOT
 * redirect on failure — the caller decides what to do with a `false`.
 */
export function refreshToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch('/auth/refresh', { method: 'POST', credentials: 'same-origin' })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

/**
 * Fetch wrapper with automatic 401 retry. On 401: refresh once, retry the
 * original request, otherwise redirect to the login flow.
 */
export async function apiFetch(url: string, opts?: RequestInit): Promise<Response> {
  const options: RequestInit = { ...opts, credentials: 'same-origin' };
  const res = await fetch(url, options);
  if (res.status !== 401) return res;

  const ok = await refreshToken();
  if (!ok) {
    redirectToLogin(window.location.pathname);
    return res;
  }
  return fetch(url, options);
}
